import { useTheme } from "../ThemeProvider";
import { usePropiedadIdioma } from "../../context/PropiedadIdiomaContext";

/**
 * Campo booleano explícito Sí/No.
 * undefined/null = ninguno seleccionado; true/false = respuesta.
 * Pulsar de nuevo el valor activo lo desmarca (vuelve a sin seleccionar).
 */
export default function YesNoField({
  name,
  label,
  form,
  cambiarDirecto,
  compact = false,
  labelAbove = false,
  onChangeSideEffect,
  chipStyle: chipStyleProp,
  onEnter: onEnterProp,
  onLeave: onLeaveProp,
  disabled = false,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { tv = (x) => x } = usePropiedadIdioma();

  const raw = form[name];
  // Ampliar: extras/APK a veces mandan "true"/"false" string; CRM exige vínculo Sí/No visible.
  const isYes = raw === true || raw === "true" || raw === 1 || raw === "1";
  const isNo = raw === false || raw === "false" || raw === 0 || raw === "0";

  const set = (val) => {
    if (disabled) return;
    cambiarDirecto(name, val);
    onChangeSideEffect?.(val);
  };

  const defaultChipStyle = (activo) => ({
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: "pointer",
    transition: "all 0.15s ease",
    userSelect: "none",
    lineHeight: "1.4",
    minWidth: compact ? 34 : 40,
    textAlign: "center",
  });

  const styleFn = chipStyleProp || defaultChipStyle;

  const defaultOnEnter = (e, activo) => {
    if (!activo) {
      e.currentTarget.style.background = c.chipHoverBg;
      e.currentTarget.style.color = c.chipHoverText;
      e.currentTarget.style.borderColor = c.chipHoverBorder;
    }
  };
  const defaultOnLeave = (e, activo) => {
    e.currentTarget.style.background = activo ? c.chipActiveBg : c.chipBg;
    e.currentTarget.style.color = activo ? c.chipActiveText : c.chipText;
    e.currentTarget.style.borderColor = activo ? c.chipActiveBorder : c.chipBorder;
  };

  const onEnter = onEnterProp || defaultOnEnter;
  const onLeave = onLeaveProp || defaultOnLeave;

  const buttons = (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <button
        type="button"
        onClick={() => set(isYes ? null : true)}
        disabled={disabled}
        style={{ ...styleFn(isYes), cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
        onMouseEnter={(e) => !disabled && onEnter(e, isYes)}
        onMouseLeave={(e) => !disabled && onLeave(e, isYes)}
        title={disabled ? "No tienes permiso para modificar" : (isYes ? "Clic para desmarcar" : undefined)}
      >
        {tv("Sí")}
      </button>
      <button
        type="button"
        onClick={() => set(isNo ? null : false)}
        disabled={disabled}
        style={{ ...styleFn(isNo), cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
        onMouseEnter={(e) => !disabled && onEnter(e, isNo)}
        onMouseLeave={(e) => !disabled && onLeave(e, isNo)}
        title={disabled ? "No tienes permiso para modificar" : (isNo ? "Clic para desmarcar" : undefined)}
      >
        {tv("No")}
      </button>
    </div>
  );

  if (!label) return buttons;

  if (labelAbove) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: c.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
          }}
        >
          {label ? tv(label) : null}
        </span>
        {buttons}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 4 : 6 }}>
      <span
        style={{
          fontSize: compact ? 12 : 13,
          color: c.textPrimary,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {label ? tv(label) : null}
      </span>
      {buttons}
    </div>
  );
}
